// Is the link to this host healthy, is the host's own PBX healthy, and what do its logs say.
//
// Kevin, 2026-08-20: "add to the pbx host detail page, a diagnostics tab. it should graph the latency
// between the cloud and the agent, graph the connectivity between the agent and freepbx, and the
// freepbx logs."
//
// THREE DIFFERENT QUESTIONS, and they fail independently — which is the reason they sit together:
//
//   CLOUD → AGENT is our own reachability, and the only one of the three the agent cannot measure.
//     Sampled from the REQUEST path — near the operator and near the host — because the per-minute cron
//     runs in whatever colo Cloudflare gives it, which turned out to be Mumbai: 270-1100ms for a call
//     that measures 86-172ms from a nearby colo (AGENT_ASKS #30). Cron samples still answer "did it
//     answer at all", which geography does not distort.
//
//   AGENT → FREEPBX is the AMI socket and the FreePBX database, as the agent reports them. A host can
//     answer us in 40ms with Asterisk unreachable behind it; those are not the same outage and must
//     not share a line on a chart.
//
//   THE LOGS are the only thing here that says WHY. Wired through AGENT_ASKS #26, which shipped
//     agent-side at v0.36.x and whose cloud half was never built — so until now reading a host's log
//     meant an SSH session.
//
// UNKNOWN IS DRAWN AS UNKNOWN. A minute the probe could not complete leaves `amiOk: null`, and a grey
// band says "we could not ask" where a red one would invent an outage. Every graph here has three
// states, not two.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { api } from '../../lib/api.js';
import LoadingSpinner from '../shared/LoadingSpinner.jsx';

const WINDOWS = [
  { hours: 1, label: '1 hr' },
  { hours: 6, label: '6 hrs' },
  { hours: 24, label: '24 hrs' },
  { hours: 48, label: '48 hrs' },
];

const clock = (iso) => {
  // D1 hands back 'YYYY-MM-DD HH:MM:SS' in UTC with no zone marker, which `new Date()` reads as LOCAL
  // time — six hours out in MDT, and the bug that made every Diagnostics row read "just now" once
  // before (see lib/time.js). The T and the Z are not decoration.
  const d = new Date(`${String(iso).replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// A run of consecutive samples sharing one state, for the band charts. Drawn as spans rather than
// points because "AMI was down from 02:14 to 02:19" is the sentence an operator needs, and a scatter
// of dots does not say it.
function runs(points, pick) {
  const out = [];
  for (const p of points) {
    const state = pick(p);
    const last = out[out.length - 1];
    if (last && last.state === state) { last.to = p.at; last.n += 1; } else {
      out.push({ state, from: p.at, to: p.at, n: 1 });
    }
  }
  return out;
}

const BAND = {
  up: 'bg-green-500',
  down: 'bg-red-500',
  unknown: 'bg-gray-300',
};
const BAND_WORD = { up: 'up', down: 'down', unknown: 'could not ask' };

function Band({ title, sub, points, pick }) {
  const spans = runs(points, pick);
  const total = points.length || 1;
  const worst = spans.filter((s) => s.state === 'down');
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <h4 className="text-sm font-medium text-gray-700">{title}</h4>
        <span className="text-xs text-gray-500">
          {worst.length === 0
            ? 'no outage in this window'
            : `${worst.reduce((n, s) => n + s.n, 0)} min down`}
        </span>
      </div>
      {sub && <p className="text-xs text-gray-400">{sub}</p>}
      <div className="mt-1.5 flex h-4 w-full overflow-hidden rounded" role="img" aria-label={title}>
        {spans.map((s, i) => (
          <div
            key={i}
            className={BAND[s.state]}
            style={{ width: `${(s.n / total) * 100}%` }}
            title={`${BAND_WORD[s.state]} — ${clock(s.from)}${s.n > 1 ? ` to ${clock(s.to)}` : ''} (${s.n} min)`}
          />
        ))}
      </div>
    </div>
  );
}

export default function PbxHostDiagnostics({ agentId, hostName }) {
  const [hours, setHours] = useState(6);

  // The probe history. Written by the cron, never by this page — see the header.
  const probes = useQuery({
    queryKey: ['agent', agentId, 'probes', hours],
    queryFn: () => api.get(`/agents/${agentId}/probes`, { hours }),
    refetchInterval: 60_000,
  });

  // Live, for the "right now" line above the history.
  const health = useQuery({
    queryKey: ['agent', agentId, 'health'],
    queryFn: () => api.get(`/agents/${agentId}/proxy/health`),
    refetchInterval: 30_000,
    retry: false,
  });

  const points = probes.data?.points || [];
  const summary = probes.data?.summary || null;
  // THE AXIS BELONGS TO THE REQUEST PATH. Cron samples are taken from whatever colo Cloudflare gave
  // that minute — Mumbai, in practice — and read 270-1100ms for a call the request path measures at
  // 86-172ms (AGENT_ASKS #30). Mixing them would swing the line by 700ms depending on who was looking.
  // The cron's samples still carry the up/down bands below, because geography does not distort whether
  // a host answered.
  //
  // A minute with no answer is a GAP, not a zero: recharts draws null as a break in the line, which is
  // the truth. Plotting 0ms would read as the fastest minute of the day.
  const chart = points
    .filter((p) => p.source === 'request')
    .map((p) => ({ at: p.at, t: clock(p.at), ms: p.reachable ? p.latencyMs : null }));

  const skew = points.filter((p) => p.skewSeconds != null).slice(-1)[0]?.skewSeconds ?? null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-gray-600">
          One probe a minute from Verso to {hostName || 'this host'}, recorded whether or not anybody is
          looking.
        </p>
        <div className="flex gap-1">
          {WINDOWS.map((w) => (
            <button
              key={w.hours}
              type="button"
              onClick={() => setHours(w.hours)}
              className={`rounded-md px-2 py-1 text-xs ${
                hours === w.hours ? 'bg-blue-600 font-medium text-white' : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {/* ---- Cloud → agent ---- */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Verso → agent round trip</h3>
            <p className="mt-0.5 text-xs text-gray-500">
              Timed in the Worker while serving a page — near you and near the host. Not your own
              browser's connection, and not the once-a-minute cron, which runs wherever Cloudflare
              schedules it.
            </p>
          </div>
          {summary?.latency && (
            <dl className="flex gap-4 text-right text-xs">
              <div><dt className="text-gray-400">now</dt><dd className="font-medium text-gray-900">{summary.latency.current}ms</dd></div>
              <div><dt className="text-gray-400">median</dt><dd className="text-gray-700">{summary.latency.median}ms</dd></div>
              <div><dt className="text-gray-400">worst</dt><dd className="text-gray-700">{summary.latency.max}ms</dd></div>
              {summary.cron && (
                // The same call from the cron's colo, kept visible and kept off the axis. A jump here
                // with a flat line above it means the scheduler moved, not that the host did.
                <div title="The per-minute cron's own samples, measured from whichever Cloudflare colo ran it">
                  <dt className="text-gray-400">from cron</dt>
                  <dd className="text-gray-500">{summary.cron.median}ms</dd>
                </div>
              )}
            </dl>
          )}
        </div>

        {probes.isLoading ? (
          <div className="py-8"><LoadingSpinner /></div>
        ) : !points.length ? (
          // Said precisely, because there are two reasons for an empty chart and only one is a problem.
          <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            No probes recorded in this window. Samples come from operator page loads and from the
            once-a-minute cron, so a host nobody has opened recently and that the cron cannot reach has
            none — and an established one with none means the probe stopped.
          </p>
        ) : (
          <>
            <div className="mt-3 h-40">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chart} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="t" tick={{ fontSize: 10, fill: '#94a3b8' }} interval="preserveStartEnd" minTickGap={40} />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} width={38} unit="ms" />
                  <Tooltip
                    contentStyle={{ fontSize: 12 }}
                    formatter={(v) => [v == null ? 'no answer' : `${v}ms`, 'round trip']}
                  />
                  {/* `connectNulls={false}` is the point: an unreachable minute breaks the line. */}
                  <Area type="monotone" dataKey="ms" stroke="#2563eb" fill="#dbeafe" connectNulls={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            {summary && summary.samples < summary.expected * 0.9 && (
              <p className="mt-2 text-xs text-amber-800">
                {summary.samples} samples where {summary.expected} were expected — the gaps are minutes
                the probe did not run, not minutes the host was fine.
              </p>
            )}
            {(summary?.colos || []).length > 0 && (
              // Named rather than implied. A series is only comparable with itself while it is measured
              // from the same place, and this is the field that says whether it was.
              <p className="mt-1 text-xs text-gray-400">
                Measured from {summary.colos.map((c) => `${c.colo} (${c.source}, ${c.samples})`).join(' · ')}
              </p>
            )}
            {!chart.length && (
              <p className="mt-2 text-xs text-amber-800">
                Nothing measured near this host yet — the line needs a page load to sample from, and the
                cron's own numbers are deliberately kept off this axis because they are timed from
                wherever Cloudflare ran it.
              </p>
            )}
          </>
        )}
      </div>

      {/* ---- Agent → the PBX behind it ---- */}
      {/*
        WHICH PBX, asked of the host. `/health` carries a top-level `backend` field ('aria' or
        'freepbx') and on an Aria host it OMITS `mariadb` and `ami` entirely — those probes are
        skipped because "they are not down on such a host, they are not part of the deployment".

        Before this branch existed the panel read `health.data?.ami?.ok` on every host, and on an
        Aria host that is `undefined` → falsy → it rendered a red **down** for AMI and for the
        database. Not grey, not "could not ask": a confident red claim that a perfectly healthy
        host had a dead Asterisk socket and a dead MariaDB, neither of which is installed on it.
        That is the exact "operator hunting for a database that was never meant to exist" the agent
        author asked us to avoid, and it was the loudest thing on the page.

        Absence of `backend` means FreePBX, not Aria — the field arrived with the Aria work, so
        every older agent legitimately omits it and every one of those hosts is FreePBX.
      */}
      {(health.data?.backend === 'aria') ? (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-gray-900">Agent → Aria</h3>
          <p className="mt-0.5 text-xs text-gray-500">
            This host runs aria-cloud-pbx — Rust and Postgres, with its own SIP stack. There is no
            Asterisk and no FreePBX database behind it, so the agent has one dependency here rather
            than two, and it is the Aria API.
          </p>

          <div className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-gray-600">Aria API right now</span>
              <span className={health.error ? 'text-gray-500' : health.data?.aria?.ok ? 'text-green-700' : 'text-red-700'}>
                {health.isLoading ? 'checking…' : health.error ? 'could not ask' : health.data?.aria?.ok ? 'ok' : 'down'}
              </span>
            </div>
            {health.data?.aria?.tenantId && (
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-gray-600">Tenant</span>
                {/* Aria scopes almost every route by tenant and the agent pins exactly one. Worth
                    showing: it is the difference between reading this customer's extensions and
                    another's, and nothing else on this page would reveal which one is pinned. */}
                <span className="font-mono text-xs text-gray-700">{health.data.aria.tenantId}</span>
              </div>
            )}
          </div>

          {points.length > 0 && (
            <div className="mt-4 space-y-3">
              <Band
                title="Aria API"
                sub="The agent's one dependency on this host. On an Aria host the probe's verdict IS this socket."
                points={points}
                pick={(p) => (p.healthy == null ? 'unknown' : p.healthy ? 'up' : 'down')}
              />
              <Band
                title="Agent reachable"
                sub="Whether the probe got any answer at all — the grey in the band above is this."
                points={points}
                pick={(p) => (p.reachable ? 'up' : 'down')}
              />
              <p className="text-xs text-gray-400">
                Green up · red down · grey we could not ask. Hover a band for the minutes it covers.
              </p>
              <p className="text-xs text-gray-400">
                No AMI or FreePBX-database band here, and that is not a gap — this PBX has neither.
              </p>
            </div>
          )}
          <ClockSkewNote skew={skew} />
        </div>
      ) : (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-gray-900">Agent → FreePBX</h3>
        <p className="mt-0.5 text-xs text-gray-500">
          The two sockets the agent needs on the host itself. A host can answer Verso in 40ms with
          Asterisk unreachable behind it.
        </p>

        <div className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-gray-600">AMI right now</span>
            <span className={health.error ? 'text-gray-500' : health.data?.ami?.ok ? 'text-green-700' : 'text-red-700'}>
              {health.isLoading ? 'checking…' : health.error ? 'could not ask' : health.data?.ami?.ok ? 'ok' : 'down'}
            </span>
          </div>
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-gray-600">Database right now</span>
            <span className={health.error ? 'text-gray-500' : health.data?.mariadb?.ok ? 'text-green-700' : 'text-red-700'}>
              {health.isLoading ? 'checking…' : health.error ? 'could not ask' : health.data?.mariadb?.ok ? 'ok' : 'down'}
              {health.data?.mariadb?.extensions != null && (
                <span className="ml-1 text-xs text-gray-500">· {health.data.mariadb.extensions} extensions</span>
              )}
            </span>
          </div>
        </div>

        {points.length > 0 && (
          <div className="mt-4 space-y-3">
            <Band
              title="AMI"
              sub="Asterisk Manager Interface — the socket every dialplan read and write goes through."
              points={points}
              pick={(p) => (p.amiOk == null ? 'unknown' : p.amiOk ? 'up' : 'down')}
            />
            <Band
              title="FreePBX database"
              sub="Where extensions, routes and mailboxes live. Config writes land here before a reload."
              points={points}
              pick={(p) => (p.dbOk == null ? 'unknown' : p.dbOk ? 'up' : 'down')}
            />
            <Band
              title="Agent reachable"
              sub="Whether the probe got any answer at all — the grey in the two bands above is this."
              points={points}
              pick={(p) => (p.reachable ? 'up' : 'down')}
            />
            <p className="text-xs text-gray-400">
              Green up · red down · grey we could not ask. Hover a band for the minutes it covers.
            </p>
          </div>
        )}

        <ClockSkewNote skew={skew} />
      </div>
      )}

      <HostLogs agentId={agentId} />
    </div>
  );
}

// A clock difference big enough to make every log window wrong. Shared by both backend branches
// because it is a property of the host, not of the PBX behind it, and nothing else on this page
// would reveal it.
function ClockSkewNote({ skew }) {
  if (skew == null || Math.abs(skew) <= 30) return null;
  return (
    <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
      This host&apos;s clock is {Math.abs(skew)}s {skew > 0 ? 'ahead of' : 'behind'} ours. Log
      timestamps and any since/until window you set below are stamped in its time, not yours.
    </p>
  );
}

// The logs. AGENT_ASKS #26's cloud half, finally.
//
// TWO FIELDS CARRY MEANING THAT MUST NOT BE FLATTENED:
//   `available: false` is PROBED, with a reason and often a fix. fail2ban's log is root-only on both
//     hosts today — a permission problem, not an absence, and the two are fixable in different ways.
//   `truncated: true` means the 8 MB per-request ceiling stopped the walk, NOT that the window was
//     empty. `asterisk/full` has reached 472 MB on a live host, so this is the common case rather than
//     the exotic one, and reading it as "nothing there" is how somebody concludes a host is quiet.
function HostLogs({ agentId }) {
  const [source, setSource] = useState(null);
  const [filter, setFilter] = useState('');
  const [applied, setApplied] = useState('');

  const sources = useQuery({
    queryKey: ['agent', agentId, 'log-sources'],
    queryFn: () => api.get(`/agents/${agentId}/proxy/logs`),
    retry: false,
  });

  const list = sources.data?.sources || sources.data || [];
  const rows = Array.isArray(list) ? list : Object.entries(list).map(([name, v]) => ({ name, ...v }));
  const chosen = source ?? rows.find((r) => r.available !== false)?.name ?? rows[0]?.name ?? null;
  const chosenRow = rows.find((r) => r.name === chosen) || null;

  const lines = useQuery({
    queryKey: ['agent', agentId, 'logs', chosen, applied],
    queryFn: () => api.get(`/agents/${agentId}/proxy/logs/${encodeURIComponent(chosen)}`, {
      lines: 200, ...(applied ? { filter: applied } : {}),
    }),
    enabled: Boolean(chosen) && chosenRow?.available !== false,
    retry: false,
  });

  // THE CONTENT IS `entries`. `lines` is the echoed REQUEST COUNT — reading it first rendered the
  // literal string "200" into the log pane on the first live look (2026-08-20). Guessing a field by
  // hopeful name is the same mistake as the metric keys in AGENT_ASKS #29, one response shape later.
  //
  // Non-arrays are discarded rather than coerced: a number or an object stringified into the pane looks
  // like log output, and there is no reading of it that is true.
  const entries = Array.isArray(lines.data?.entries) ? lines.data.entries : [];
  const more = lines.data?.hasMore === true;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <h3 className="text-sm font-semibold text-gray-900">Logs</h3>
      <p className="mt-0.5 text-xs text-gray-500">
        Read from the host on demand. Secrets are redacted before the lines leave it — the key stays
        visible, the value does not — and the filter is applied after that, so it cannot be used to
        confirm what was removed.
      </p>

      {sources.isLoading && <div className="py-6"><LoadingSpinner /></div>}
      {sources.error && (
        <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Could not ask the host which logs it has: {sources.error.body?.error || sources.error.message}
        </p>
      )}

      {rows.length > 0 && (
        <>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {rows.map((r) => (
              <button
                key={r.name}
                type="button"
                disabled={r.available === false}
                onClick={() => setSource(r.name)}
                title={r.available === false ? (r.reason || 'not readable on this host') : undefined}
                className={`rounded-md px-2 py-1 text-xs ${
                  r.name === chosen ? 'bg-blue-600 font-medium text-white'
                    : r.available === false ? 'border border-gray-200 text-gray-300'
                      : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {r.name}
              </button>
            ))}
            <form
              className="ml-auto flex items-center gap-2"
              onSubmit={(e) => { e.preventDefault(); setApplied(filter.trim()); }}
            >
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="filter, e.g. an IP or extension"
                className="w-56 rounded-md border border-gray-300 px-2 py-1 text-xs"
              />
              <button type="submit" className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50">
                Filter
              </button>
            </form>
          </div>

          {/* A source the host cannot read is not an empty log, and the reason is the actionable half. */}
          {chosenRow?.available === false && (
            <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <span className="font-medium">{chosen}</span> is not readable on this host
              {chosenRow.reason ? `: ${chosenRow.reason}` : '.'}
              {chosenRow.fix && <span className="mt-1 block">Fix: {chosenRow.fix}</span>}
            </p>
          )}

          {lines.error && (
            <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {lines.error.body?.error || lines.error.message}
            </p>
          )}

          {lines.data?.truncated && (
            <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              We stopped reading at the size limit, so older lines in this window were never examined —
              this is not an empty result. Narrow it with a filter or a time window.
            </p>
          )}

          {lines.isFetching ? (
            <div className="py-6"><LoadingSpinner /></div>
          ) : chosenRow?.available !== false && (
            <>
              <pre className="mt-3 max-h-96 overflow-auto rounded-md bg-gray-900 p-3 text-[11px] leading-relaxed text-gray-100">
                {entries.length
                  ? entries.join('\n')
                  : `Nothing in ${chosen}${applied ? ` matching “${applied}”` : ''} in the window read.`}
              </pre>
              <p className="mt-1.5 text-xs text-gray-400">
                Newest first, {entries.length} line{entries.length === 1 ? '' : 's'}
                {more && ' — there is more further back'}
                {lines.data?.redacted && ' · secrets redacted host-side'}
                {lines.data?.window?.note && ` · ${lines.data.window.note}`}
              </p>
            </>
          )}
        </>
      )}
    </div>
  );
}
